// What the Exams pages work out from the relay's answers (RFC-048 "The Exams
// page", byj-1wv.56.5): a question's bar in words, where a student stands on
// a question, a grade's score as it counts, and the submissions as CSV; where
// an assignment's window stands and what of a draft needs a look (byj-1wv.51.12).
import type { ExamAssignmentView, ExamAttempt, ExamDraft, ExamGrade, ExamQuestion, ExamSubmission } from "./api";
import { t, phrases, word } from "./i18n";

/** A question's bar, as the exam sheet states it. */
export function barWords(c: ExamQuestion["criteria"]): string {
  if (!c) return "";
  return [
    c.agent && t("exams:barAgent", { a: c.agent }),
    c["max-tool-calls"] != null && t("exams:barCalls", { n: c["max-tool-calls"] }),
    c["max-cost-usd"] != null && phrases.barCost(c["max-cost-usd"]),
    c["max-seconds"] != null && t("exams:barSeconds", { n: c["max-seconds"] }),
    c.weight != null && c.weight !== 1 && t("exams:barWeight", { n: c.weight }),
  ]
    .filter(Boolean)
    .join(" · ");
}

/** The score a grade counts for: its override, else the grader's. */
export const scoreOf = (g: ExamGrade) => g.override ?? g.score;

export type QuestionState = "not-started" | "sitting" | "submitted" | "grading" | "released";

/** Where a student stands on a question: from their attempt at it and its last hand-in. */
export function questionState(a: ExamAttempt | undefined): QuestionState {
  if (!a) return "not-started";
  const handed = a.submissions.filter((s) => s.uploaded);
  if (handed.length === 0) return "sitting";
  const last = handed[handed.length - 1]!;
  if (last.grade?.released) return "released";
  return last.grade ? "grading" : "submitted";
}

/** A hand-in's state, for its instructor. */
export function gradeState(s: ExamSubmission): "waiting" | "graded" | "released" {
  if (!s.grade) return "waiting";
  return s.grade.released ? "released" : "graded";
}

/** A hand-in's dispute still waiting for its answer. */
export const openDispute = (s: ExamSubmission) => (s.disputes ?? []).find((d) => !d.answered_at);

export type WindowState = "not-yet" | "open" | "closed";

/** Where an assignment's window stands at `now`, from the times the relay set (byj-1wv.51.12). */
export function windowState(a: Pick<ExamAssignmentView, "opens_at" | "closes_at">, now: number): WindowState {
  if (a.opens_at && Date.parse(a.opens_at) > now) return "not-yet";
  if (a.closes_at && Date.parse(a.closes_at) <= now) return "closed";
  return "open";
}

export type ExamStage = "write" | "check" | "fix" | "assign" | "grade";

/**
 * Where an exam stands in being set, for the one thing to do next
 * (byj-1wv.51.12). Never published: its questions to write, then its kit to
 * make and check on the instructor's machine, or what that check found to
 * fix. Published: hand-ins to grade first, as students wait on them; then a
 * draft to fix or check again; then a class to assign it to. Null: nothing
 * waits on the instructor.
 */
export function examStage(e: { published: boolean; assigned: number; queued: number; written: boolean }, d: ExamDraft | null): ExamStage | null {
  const notice = draftNotice(d);
  if (!e.published) {
    if (!e.written) return "write";
    return notice === "invalid" ? "fix" : "check";
  }
  if (e.queued > 0) return "grade";
  if (notice === "invalid") return "fix";
  if (notice) return "check";
  return e.assigned === 0 ? "assign" : null;
}

/** What of a draft the instructor should look at: none pulled, not valid, or edited since it was pulled; else nothing. */
export function draftNotice(d: ExamDraft | null): "not-pulled" | "invalid" | "stale" | null {
  if (!d) return null;
  if (!d.status) return "not-pulled";
  if (!d.status.ok) return "invalid";
  return d.status.draft_updated_at !== d.updated_at ? "stale" : null;
}

function cell(v: unknown): string {
  const s = v == null ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** The submissions as CSV, a row each: who, which question, with what, when, and the grade. */
export function submissionsCsv(subs: ExamSubmission[]): string {
  const head = ["student", "email", "question", "agent", "n", "submitted_at", "late", "score", "override", "override_note", "released", "submission"];
  const rows = subs.map((s) => [
    s.student?.name,
    s.student?.email,
    s.question,
    s.agent,
    s.n,
    s.submitted_at,
    s.late,
    s.grade?.score,
    s.grade?.override,
    s.grade?.override_note,
    s.grade?.released ?? false,
    s.id,
  ]);
  return [head, ...rows].map((r) => r.map(cell).join(",")).join("\n") + "\n";
}

/** A file to save, from text: a link clicked and let go. */
export function download(name: string, text: string, type = "text/csv") {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** A grading signal's name in the page's words (RFC-047's Prism items, the fluency rubric's rules and judged ones); an unknown one by its id. */
export const signalName = (id: string): string => word("exams:signal", id) ?? id;
